import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  VizCard,
  NowYouTry,
  VIZ_BTN_GHOST,
  usePrefersReducedMotion,
  useMountSequence,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// ReactionConditions — the *notation* written on a reaction arrow. Conditions
// that drive a reaction (a catalyst, heat / Δ, a named metal like Fe or Pt) are
// written ABOVE the arrow, with secondary conditions (a temperature, "heat")
// written BELOW it. A separate "NR" marker means *no reaction*: the reactants
// are shown, the arrow is struck through / labelled NR, and there is no product
// to write. This widget renders that classic notation cleanly and statically,
// then (only when a narration cue is PLAYING) reveals it part by part.
//
//   reactants  --[ over / under ]-->  products
//
// config: { reactants?, products?, over? (text ABOVE the arrow, e.g. 'Fe',
//           'catalyst', 'Pt'), under? (text BELOW, e.g. 'Δ', 'heat', '300 °C'),
//           noReaction? (true ⇒ arrow marked NR, no products), title? }
// Cue: cue.progress / cue.step walk reactants → arrow+conditions → products in
// sequence (FULL at rest, gated on cue.play); cue.highlight ∈ {over, under, nr,
// reactants, products, arrow} spotlights that part. Resilient: empty config
// falls back to a plain "A + B → C" with no conditions; never throws.

export interface ReactionConditionsConfig {
  /** Left-hand side, e.g. 'N₂ + 3 H₂' or 'A + B'. */
  reactants?: string;
  /** Right-hand side, e.g. '2 NH₃' or 'C'. Ignored when noReaction is true. */
  products?: string;
  /** Text ABOVE the arrow — the driving condition (catalyst / metal). */
  over?: string;
  /** Text BELOW the arrow — secondary condition (heat / temperature). */
  under?: string;
  /** When true: show reactants, an arrow marked 'NR', and no products. */
  noReaction?: boolean;
  title?: string;
}

interface Props {
  config?: ReactionConditionsConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// SVG canvas — a single clean horizontal row (house style proportions).
const W = 360;
const H = 150;
const MID_Y = H / 2 + 6; // vertical center of the arrow/equation line

// Reveal order: 0 = reactants, 1 = arrow + conditions, 2 = products.
type Part = 'reactants' | 'arrow' | 'products';
const PART_ORDER: Part[] = ['reactants', 'arrow', 'products'];

function str(v: unknown, fallback: string): string {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length > 0 ? s : fallback;
}

export default function ReactionConditions({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // ----- Resolve + sanitize the model ---------------------------------------
  const noReaction = config.noReaction === true;
  const reactants = str(config.reactants, 'A + B');
  const products = noReaction ? '' : str(config.products, 'C');
  // Conditions are optional; empty strings mean "nothing on that side".
  const over = typeof config.over === 'string' ? config.over.trim() : '';
  const under = typeof config.under === 'string' ? config.under.trim() : '';
  const hasOver = over.length > 0;
  const hasUnder = under.length > 0;

  // ----- Reveal state --------------------------------------------------------
  // upTo = how many parts are revealed (0..3). At rest everything is shown.
  const TOTAL = PART_ORDER.length;
  const [upTo, setUpTo] = useState<number>(hasCue ? TOTAL : 0);
  // Which single part is spotlighted (-1 = none). Driven by cue.highlight.
  const [lit, setLit] = useState<Set<Part> | null>(null);
  const interactedRef = useRef(false);

  const mark = (result?: unknown) => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.(result ?? { type: 'reaction_conditions', noReaction });
    }
  };

  // Self-demo on mount: reveal reactants → arrow → products once.
  const demo = useMountSequence(
    [
      { at: 350, run: () => setUpTo(1) },
      { at: 1050, run: () => setUpTo(2) },
      { at: 1750, run: () => setUpTo(3) },
    ],
    { skip: reduced || hasCue, onSettle: () => setUpTo(TOTAL) },
  );

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
  };

  // ----- Cue handling --------------------------------------------------------
  // Reveal only while PLAYING; at rest (paused) show the FULL equation — never a
  // one-part stub. computeCue can report a step/progress even when paused, so
  // both are gated on cue.play.
  useEffect(() => {
    if (!cue) return;
    if (cue.play === true && typeof cue.step === 'number') {
      setUpTo(Math.max(0, Math.min(TOTAL, Math.round(cue.step) + 1)));
    } else if (cue.play === true && typeof cue.progress === 'number') {
      const p = Math.max(0, Math.min(1, cue.progress));
      setUpTo(Math.max(1, Math.round(p * TOTAL)));
    } else {
      setUpTo(TOTAL);
    }
  }, [cue?.step, cue?.progress, cue?.play]);

  // highlight tokens map onto a part spotlight.
  useEffect(() => {
    const toks = cueHighlights(cue);
    if (toks.length === 0) {
      setLit(null);
      return;
    }
    const set = new Set<Part>();
    for (const t of toks) {
      if (/^(over|cataly|metal|top|above)/.test(t)) set.add('arrow');
      if (/^(under|heat|temp|below|delta|Δ)/.test(t) || t.includes('δ')) set.add('arrow');
      if (/(^nr$|no.?react)/.test(t)) set.add('arrow');
      if (t.includes('arrow') || t.includes('condition')) set.add('arrow');
      if (t.includes('react')) set.add('reactants');
      if (t.includes('prod')) set.add('products');
    }
    setLit(set.size > 0 ? set : null);
  }, [cue]);

  // Which of {over, under, nr} specifically is spotlighted (for finer emphasis).
  const litToken = useMemo(() => {
    const toks = cueHighlights(cue);
    const s = new Set<string>();
    for (const t of toks) {
      if (/^(over|cataly|metal|top|above)/.test(t)) s.add('over');
      if (/^(under|heat|temp|below)/.test(t) || t.includes('δ') || /^delta/.test(t)) s.add('under');
      if (/(^nr$|no.?react)/.test(t)) s.add('nr');
    }
    return s;
  }, [cue]);

  // ----- Visibility helpers --------------------------------------------------
  const shown = (p: Part): boolean => upTo >= PART_ORDER.indexOf(p) + 1;
  const anySpot = lit != null && lit.size > 0;
  const dim = (p: Part): number => {
    if (!shown(p)) return 0;
    if (anySpot && !lit!.has(p)) return 0.32;
    return 1;
  };
  const trans = reduced ? undefined : 'opacity 300ms ease-out';

  // ----- Geometry ------------------------------------------------------------
  // Three columns: reactants | arrow | products. Arrow sits in the middle band.
  const reactX = noReaction ? 96 : 64;
  const prodX = W - 64;
  const arrowX1 = reactX + (noReaction ? 28 : 36);
  const arrowX2 = noReaction ? arrowX1 + 92 : prodX - 36;
  const arrowMidX = (arrowX1 + arrowX2) / 2;

  const overEmph = litToken.has('over');
  const underEmph = litToken.has('under');
  const nrEmph = litToken.has('nr');

  return (
    <VizCard
      eyebrow="Reactions · Arrow notation"
      title={config.title ?? (noReaction ? 'No reaction (NR) notation' : 'Reaction conditions on the arrow')}
      subtitle={
        noReaction
          ? 'When two reactants do not react, we write NR — there is no product'
          : 'Conditions that drive a reaction are written above (and below) the arrow'
      }
      reducedMotion={reduced}
      hint={
        noReaction
          ? 'NR = no reaction. The reactants are shown, but the arrow leads to nothing.'
          : 'Above the arrow: catalyst / metal (e.g. Fe, Pt). Below: heat (Δ) or temperature.'
      }
      actions={
        <button
          type="button"
          onClick={() => {
            cancelDemo();
            setUpTo((n) => (n >= TOTAL ? 1 : n + 1));
            mark();
          }}
          className={VIZ_BTN_GHOST}
        >
          <RotateCcw size={12} /> Reveal
        </button>
      }
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full rounded-md border border-ws-border bg-ws-surface-2"
          role="img"
          aria-label={
            noReaction
              ? `${reactants} — no reaction`
              : `${reactants} reacts to ${products}${hasOver ? `, with ${over} above the arrow` : ''}${
                  hasUnder ? `, ${under} below the arrow` : ''
                }`
          }
        >
          <defs>
            <marker
              id="rc-head"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L10 5 L0 10 z" fill="context-stroke" />
            </marker>
          </defs>

          {/* ----- Reactants ----- */}
          <g opacity={dim('reactants')} style={{ transition: trans }}>
            <text
              x={reactX}
              y={MID_Y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="15"
              fontWeight="600"
              fill="var(--ws-ink)"
            >
              {reactants}
            </text>
          </g>

          {/* ----- Arrow + conditions ----- */}
          <g opacity={dim('arrow')} style={{ transition: trans }}>
            {/* Condition ABOVE the arrow */}
            {hasOver && (
              <g style={{ transition: trans }} opacity={anySpot && !litToken.has('over') && (overEmph || underEmph || nrEmph) ? 0.45 : 1}>
                <text
                  x={arrowMidX}
                  y={MID_Y - 16}
                  textAnchor="middle"
                  fontSize={overEmph ? '14' : '12.5'}
                  fontWeight={overEmph ? '700' : '600'}
                  fill={overEmph ? 'var(--ws-accent)' : 'var(--ws-accent-ink)'}
                  style={{ transition: reduced ? undefined : 'font-size 200ms ease-out' }}
                >
                  {over}
                </text>
              </g>
            )}

            {/* The arrow shaft + head (struck for NR) */}
            <line
              x1={arrowX1}
              y1={MID_Y}
              x2={arrowX2}
              y2={MID_Y}
              stroke="var(--ws-ink)"
              strokeWidth="1.75"
              markerEnd="url(#rc-head)"
            />

            {/* No-reaction marker: bold NR above + a slash through the arrow */}
            {noReaction && (
              <g style={{ transition: trans }}>
                <line
                  x1={arrowMidX - 9}
                  y1={MID_Y + 9}
                  x2={arrowMidX + 9}
                  y2={MID_Y - 9}
                  stroke="var(--ws-danger)"
                  strokeWidth={nrEmph ? '2.75' : '2.25'}
                  strokeLinecap="round"
                />
                <text
                  x={arrowMidX}
                  y={MID_Y - 16}
                  textAnchor="middle"
                  fontSize={nrEmph ? '17' : '15'}
                  fontWeight="800"
                  fill="var(--ws-danger)"
                  style={{ transition: reduced ? undefined : 'font-size 200ms ease-out' }}
                >
                  NR
                </text>
              </g>
            )}

            {/* Condition BELOW the arrow */}
            {hasUnder && (
              <g style={{ transition: trans }} opacity={anySpot && !litToken.has('under') && (overEmph || underEmph || nrEmph) ? 0.45 : 1}>
                <text
                  x={arrowMidX}
                  y={MID_Y + 22}
                  textAnchor="middle"
                  fontSize={underEmph ? '14' : '12.5'}
                  fontWeight={underEmph ? '700' : '600'}
                  fill={underEmph ? 'var(--ws-accent)' : 'var(--ws-ink-2)'}
                  style={{ transition: reduced ? undefined : 'font-size 200ms ease-out' }}
                >
                  {under}
                </text>
              </g>
            )}
          </g>

          {/* ----- Products (or struck-out blank for NR) ----- */}
          <g opacity={noReaction ? dim('arrow') : dim('products')} style={{ transition: trans }}>
            {noReaction ? (
              // Empty / struck product side: a faint dash that no product follows.
              <g opacity={0.55}>
                <line
                  x1={arrowX2 + 16}
                  y1={MID_Y + 7}
                  x2={arrowX2 + 40}
                  y2={MID_Y - 7}
                  stroke="var(--ws-ink-4)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1={arrowX2 + 16}
                  y1={MID_Y - 7}
                  x2={arrowX2 + 40}
                  y2={MID_Y + 7}
                  stroke="var(--ws-ink-4)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </g>
            ) : (
              <text
                x={prodX}
                y={MID_Y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="15"
                fontWeight="600"
                fill="var(--ws-ink)"
              >
                {products}
              </text>
            )}
          </g>
        </svg>

        {/* Part stepper — clickable, mirrors the reveal order */}
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {PART_ORDER.map((p, i) => {
            const active = upTo >= i + 1;
            const labels: Record<Part, string> = {
              reactants: '1 · Reactants',
              arrow: noReaction ? '2 · Arrow · NR' : '2 · Arrow + conditions',
              products: noReaction ? '3 · No product' : '3 · Products',
            };
            return (
              <button
                key={p}
                type="button"
                onClick={() => {
                  cancelDemo();
                  setUpTo(i + 1);
                  mark();
                }}
                className={`rounded-sm border px-2 py-1.5 text-[11px] font-medium transition-colors ${
                  active
                    ? 'border-ws-accent bg-ws-accent text-white'
                    : 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                {labels[p]}
              </button>
            );
          })}
        </div>

        {/* Condition legend */}
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Legend
            label="Above the arrow"
            value={hasOver ? over : '—'}
            sub="catalyst / metal (Fe, Pt)"
            accent="var(--ws-accent-ink)"
            emph={overEmph}
          />
          <Legend
            label="Below the arrow"
            value={hasUnder ? under : '—'}
            sub="heat (Δ) / temperature"
            emph={underEmph}
          />
          <Legend
            label={noReaction ? 'Result' : 'Direction'}
            value={noReaction ? 'NR' : '→'}
            sub={noReaction ? 'no reaction · no product' : 'reactants form products'}
            accent={noReaction ? 'var(--ws-danger)' : undefined}
            emph={nrEmph}
          />
        </div>
      </div>
    </VizCard>
  );
}

function Legend({
  label,
  value,
  sub,
  accent,
  emph,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
  emph?: boolean;
}) {
  return (
    <div
      className={`rounded-md border px-2.5 py-1.5 transition-colors ${
        emph ? 'border-ws-accent bg-ws-tile' : 'border-ws-border bg-ws-tile'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">{label}</div>
      <div className="text-sm font-semibold" style={{ color: accent ?? 'var(--ws-ink)' }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-ws-ink-3">{sub}</div>}
    </div>
  );
}
